import { useContext } from 'react'
import Head from 'next/head'
import { SanityAnyPage } from '@data/sanity/queries/types/page'
import { SeoContext } from '@lib/seo-context'

interface HeadSeoProps {
  page: SanityAnyPage
  canonicalUrl?: string
  blogPostSchema?: string
}

const HeadSeo = ({ page, canonicalUrl, blogPostSchema }: HeadSeoProps) => {
  const {
    siteTitle,
    metaTitle,
    metaDescription,
    shareTitle,
    shareDescription,
    shareGraphicUrl,
  } = useContext(SeoContext)

  // Disable indexing for non-production deployments
  const noIndex =
    !!page.noIndex || process.env.NEXT_PUBLIC_APP_ENV !== 'production'

  return (
    <Head>
      <meta charSet="utf-8" />
      <meta httpEquiv="x-ua-compatible" content="ie=edge" />
      <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      <meta name="format-detection" content="telephone=no" />
      {noIndex && <meta name="robots" content="noindex" />}

      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/apple-touch-icon.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/favicon-32x32.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/favicon-16x16.png"
      />
      <link rel="manifest" href="/site.webmanifest" />
      <link rel="mask-icon" href="/safari-pinned-tab.svg" color="" />
      <meta name="msapplication-TileColor" content="" />
      <meta name="theme-color" content="" />

      <link
        rel="preconnect"
        href="https://cdn.sanity.io"
        crossOrigin="anonymous"
      />

      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="title" property="og:title" content={shareTitle} />
      <meta name="twitter:title" content={shareTitle} />
      <meta
        name="description"
        property="og:description"
        content={shareDescription}
      />
      <meta name="twitter:description" content={shareDescription} />

      {shareGraphicUrl && (
        <>
          <meta name="image" property="og:image" content={shareGraphicUrl} />
          <meta name="twitter:image" content={shareGraphicUrl} />
        </>
      )}

      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="og:site_name" content={siteTitle} />

      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {blogPostSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: blogPostSchema }}
        />
      )}
    </Head>
  )
}

export default HeadSeo
